import React from "react";
import Layout from "@/components/Layout";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { Download, Trash2, Palette } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import ShareManager from "@/components/ShareManager";

export default function Settings() {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  const downloadJson = async () => {
    const { data } = await api.get("/cases");
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "fellowlog_data.json"; a.click();
    URL.revokeObjectURL(url);
    toast.success("Data exported");
  };

  const deleteAccount = async () => {
    if (!window.confirm("Delete all your data and sign out? This cannot be undone.")) return;
    try {
      // Delete all cases individually (endpoint-safe)
      const { data } = await api.get("/cases");
      await Promise.all((data || []).map((c) => api.delete(`/cases/${c.id}`).catch(() => {})));
      toast.success("Data cleared");
      await logout();
      nav("/");
    } catch {
      toast.error("Could not delete data");
    }
  };

  return (
    <Layout>
      <div className="mb-8">
        <h1 className="heading text-3xl md:text-4xl text-[#3F4E5B]">Settings</h1>
        <p className="subtle mt-2">App preferences and account management.</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="lg:col-span-2">
          <ShareManager />
        </div>
        <div className="neu-card p-6 md:p-8" data-testid="settings-theme">
          <div className="flex items-center gap-2 mb-3">
            <Palette className="w-5 h-5 text-neu-accent" />
            <div className="heading text-lg text-[#3F4E5B]">Appearance</div>
          </div>
          <p className="text-sm subtle mb-4">Current theme: Neumorphic light. Dark mode coming soon.</p>
          <button className="btn-neu" disabled>
            Light theme (default)
          </button>
        </div>

        <div className="neu-card p-6 md:p-8" data-testid="settings-export">
          <div className="flex items-center gap-2 mb-3">
            <Download className="w-5 h-5 text-neu-accent" />
            <div className="heading text-lg text-[#3F4E5B]">Data export</div>
          </div>
          <p className="text-sm subtle mb-4">Download a full JSON copy of all your cases.</p>
          <button onClick={downloadJson} className="btn-accent inline-flex items-center gap-2" data-testid="settings-download-json">
            <Download className="w-4 h-4" /> Download JSON
          </button>
        </div>

        <div className="neu-card p-6 md:p-8 lg:col-span-2" data-testid="settings-danger">
          <div className="flex items-center gap-2 mb-3">
            <Trash2 className="w-5 h-5 text-red-600" />
            <div className="heading text-lg text-[#3F4E5B]">Danger zone</div>
          </div>
          <p className="text-sm subtle mb-4">
            This will erase all cases logged for {user?.email} and sign you out. This action is irreversible.
          </p>
          <button
            onClick={deleteAccount}
            className="inline-flex items-center gap-2 text-white bg-red-600 hover:bg-red-700 px-4 py-2 rounded-xl font-semibold transition-colors"
            data-testid="settings-delete-account"
          >
            <Trash2 className="w-4 h-4" /> Delete all my data
          </button>
        </div>
      </div>
    </Layout>
  );
}
